import React from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { LayoutContext } from '../types/translations';
import { loadBlogPosts } from '../blog';
import { Post } from '../types/content';

const BlogView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { language, texts } = useOutletContext<LayoutContext>();
  const [post, setPost] = React.useState<Post | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadPost = async () => {
      try {
        const posts = await loadBlogPosts(language);
        const currentPost = posts[parseInt(id || '0', 10)];
        setPost(currentPost || null);
      } catch (error) {
        console.error('Error loading blog post:', error);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] text-gray-900 dark:text-white transition-colors duration-200">
        <div className="container mx-auto px-6 pt-32">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] text-gray-900 dark:text-white transition-colors duration-200">
        <div className="container mx-auto px-6 pt-32">
          <h1 className="text-4xl font-bold mb-6">{texts.blog.none}</h1>
          <button onClick={() => navigate('/blog')} className="text-blue-600 dark:text-blue-400 hover:underline">
            {texts.blog.back}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] text-gray-900 dark:text-white transition-colors duration-200">
      <div className="container mx-auto px-6 pt-32">
        <button onClick={() => navigate('/blog')} className="text-blue-600 dark:text-blue-400 hover:underline mb-6">
          {texts.blog.back}
        </button>
        <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{post.date}</p>
        {post.content.map((section, index) => (
          <div key={index} className="mb-6">
            {section.heading && (<h2 className="text-2xl font-bold mb-2">{section.heading}</h2>)}
            {section.body && (
              <div className="text-gray-700 dark:text-gray-300 leading-relaxed prose dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: section.body }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogView;
